/**
 * Lightweight screen wake lock helper. Uses feature detection and falls back gracefully
 * when the Wake Lock API is unavailable or revoked.
 */
class WakeLockService {
  private wakeLockSentinel: WakeLockSentinel | null = null;
  private visibilityHandlerBound = false;

  private isSupported(): boolean {
    return typeof navigator !== "undefined" && !!navigator.wakeLock?.request;
  }

  /**
   * Request a screen wake lock. Returns a release function if acquired, otherwise null.
   */
  async requestScreenWakeLock(): Promise<(() => Promise<void>) | null> {
    if (!this.isSupported()) {
      console.log("ℹ️ Wake lock not supported in this browser");
      return null;
    }

    try {
      console.log("⏳ Requesting wake lock (screen)...");
      this.wakeLockSentinel = await navigator.wakeLock!.request("screen");
      console.log("🔒 Wake lock acquired (screen)");
      this.attachVisibilityHandler();

      const release = async () => {
        try {
          await this.wakeLockSentinel?.release();
          console.log("🔓 Wake lock released");
        } catch (err) {
          console.warn("Wake lock release failed", err);
        } finally {
          this.wakeLockSentinel = null;
        }
      };

      return release;
    } catch (err: any) {
      // AbortError/NotAllowedError are common when the lock is revoked or disallowed
      console.warn("Wake lock request failed", err?.name || err);
      return null;
    }
  }

  /**
   * Re-request wake lock on visibility change if it was previously held.
   */
  private attachVisibilityHandler(): void {
    if (this.visibilityHandlerBound) return;

    const handler = async () => {
      if (!this.wakeLockSentinel) return;
      if (document.visibilityState === "visible") {
        try {
          this.wakeLockSentinel = await navigator.wakeLock!.request("screen");
          console.log("🔁 Wake lock re-acquired after visibility change");
        } catch (err) {
          console.warn("Wake lock re-request failed", err);
          this.wakeLockSentinel = null;
        }
      }
    };

    document.addEventListener("visibilitychange", handler);
    this.visibilityHandlerBound = true;
  }
}

export default new WakeLockService();
